"use client";
import React, { Fragment, useEffect, useState } from "react";

import { useRouter } from "next/navigation";

import { LoadingOutlined } from "@ant-design/icons";
import { Col, Row, Spin } from "antd";
import { useDebounce } from "use-debounce";

import BrowserSearchbar from "@/component/BrowserSearchbar";
import StockCard from "@/component/stockCard/page";
import { blueSearch, greySearch } from "@/lib/imageIcon";
import typeSenseSearch from "@/lib/typesense";
import Waypoint from "@/lib/waypoint";

export default function Home() {
  const router = useRouter();
  const antIcon = <LoadingOutlined className="fs-24" spin />;

  const [searchToggle, _searchToggle] = useState(false);
  const [isMac, setIsMac] = useState(false);
  const [stocks, setStocks] = useState([]);
  const [page, setPage] = useState(0);
  const [isHover, setIsHover] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [loadData, setLoadData] = useState(false);
  const [advanceSearchCount, setAdvanceSearchCount] = useState(0);
  const [debouncedValue] = useDebounce(searchValue, 300);

  useEffect(() => {
    setIsMac(navigator.platform.indexOf("Mac") > -1);
    handleSearch("", 0);
  }, []);

  const handleSearch = async (value, pageNum) => {
    try {
      setLoadData(true);
      const searchData = await typeSenseSearch(value, pageNum);
      setAdvanceSearchCount(searchData?.found || 0);
      const documents = searchData?.hits?.map((hit) => hit.document) || [];

      if (pageNum === 0) {
        setStocks(documents);
      } else {
        setStocks((prev) => [...prev, ...documents]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Search error:", error);
    } finally {
      setLoadData(false);
    }
  };

  useEffect(() => {
    if (!debouncedValue) {
      setStocks([]);
      handleSearch("", 0);
      return;
    }
    setPage(0);
    handleSearch(debouncedValue, 0);
  }, [debouncedValue]);

  useEffect(() => {
    if (page > 0) {
      handleSearch(debouncedValue, page + 1);
    }
  }, [page]);

  return (
    <Fragment>
      {searchToggle && (
        <div
          className="advance-search-bg-blue p-0"
          style={{ height: document.documentElement.scrollHeight }}
        />
      )}
      {searchToggle && (
        <BrowserSearchbar
          _searchToggle={_searchToggle}
          searchToggle={searchToggle}
          setPage={setPage}
          page={page}
        />
      )}

      <div className="search-container d-flex flex-column align-items-center">
        <div className="d-flex align-items-center">
          <div>
            <h1 className="search-title">Clear Confident Investing</h1>
            <div className="search-subtitle">
              Search for your favorite stocks with precision-powered insights.
            </div>
          </div>
        </div>
        <div className="w-100 m-0">
          <div
            className="d-flex header-search-div w-100 h-56 m-0 p-16 justify-content-between cursor-pointer"
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
          >
            <div className="d-flex m-0 w-90 align-items-center">
              <img
                alt="Search"
                src={isHover ? blueSearch : greySearch}
                width={16}
                height={16}
              />
              <div className="w-100">
                <input
                  className="mb-0 search-text responsive-text w-100"
                  placeholder="Start typing a stock name or ticker (AAPL, MSFT, Apple, Microsoft)"
                  value={searchValue}
                  autoCapitalize="off"
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </div>
            </div>
            <div className="search-command-div">
              <p className="mb-0 search-command">
                {isMac ? "CMD" : "CTRL"} + K
              </p>
            </div>
          </div>
          <div className="d-flex justify-content-center">
            <span className="search-subtitle">
              Over 50,000 stocks from over 50 countries
            </span>
          </div>
        </div>
        <div className="mt-8 w-100">
          <div className="stock-container">
            <h2 className="stock-title h4-style">Top searched stocks</h2>
            <div className="mt-16">
              <Row gutter={[24, 24]}>
                {stocks?.map((stock, index) => (
                  <Col
                    key={`${stock?.id}-${index}`}
                    xs={24}
                    sm={12}
                    md={12}
                    lg={8}
                  >
                    <StockCard
                      key={stock?.id}
                      stocks={stock}
                      router={router}
                      _searchToggle={_searchToggle}
                      isMac={isMac}
                    />
                  </Col>
                ))}
                {loadData && (
                  <Col xs={24} className="text-center">
                    <Spin indicator={antIcon} size="large" />
                  </Col>
                )}
                <Waypoint
                  stocks={stocks}
                  advanceSearchCount={advanceSearchCount}
                  page={page}
                  setPage={setPage}
                />
              </Row>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}
